import { reportingTransactions } from "../../../lib/allocations";
import { Suspense, useMemo, useState } from "react";
import { CalendarDays, ChartLine, ChevronDown } from "lucide-react";
import type { Transaction, OwnerFilter } from "../../../types";
import { isSpendable, money, reportableBase } from "../../../lib/finance";
import { Private } from "../../../components/Privacy";
import type { DailyChartStyle } from "../../../app/use-overview-preferences";
import DailySpendingHeatmap, { type HeatmapDay } from "./DailySpendingHeatmap";
import { lazyScreen } from "../../../app/lazy-screen";
import { roundMoney } from "../../../lib/money-math";

const DailySpendingPlot = lazyScreen(() => import("./DailySpendingPlot"), { compact: true });

interface SpendingChartProps {
  transactions: Transaction[];
  currentMonth: string;
  owner: OwnerFilter;
  today: string;
  baseCurrency: string;
  chartStyle: DailyChartStyle;
  onChartStyleChange: (style: DailyChartStyle) => void;
  collapsed: boolean;
  onToggle: () => void;
}

const OVERALL = "";

export default function SpendingChart({
  transactions,
  currentMonth,
  owner,
  today,
  baseCurrency,
  chartStyle,
  onChartStyleChange,
  collapsed,
  onToggle,
}: SpendingChartProps) {
  const [category, setCategory] = useState(OVERALL);
  // The chart is a primary overview, including on mobile. Keep it visible on
  // first load; the disclosure still lets someone collapse it afterward.
  const monthExpenses = useMemo(
    () =>
      reportingTransactions(transactions).filter(
        (t) =>
          isSpendable(t) &&
          t.date.startsWith(currentMonth) &&
          t.reportableAmount !== 0 &&
          (owner === "All" || t.owner === owner),
      ),
    [transactions, currentMonth, owner],
  );
  // Every category with spending this month, ranked highest-first so the ones worth
  // drilling into surface at the top of the dropdown rather than being buried alphabetically.
  // Amounts use the base-currency conversion (matching the Monthly Spending card) rather than
  // each transaction's own currency, so MVR and USD expenses aren't mixed into one raw number.
  const categories = useMemo(() => {
    const totals = new Map<string, number>();
    for (const t of monthExpenses)
      totals.set(t.category, (totals.get(t.category) || 0) + Math.abs(reportableBase(t) ?? 0));
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, [monthExpenses]);
  // Reset to Overall if the previously-selected category has no spending in the new
  // month/owner filter, instead of silently showing an empty chart.
  const selected = categories.includes(category) ? category : OVERALL;
  const { dailyData, daysInMonth } = useMemo(() => {
    const [year, month] = currentMonth.split("-").map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    // Days that haven't happened yet are left out so the calendar shows them as upcoming rather
    // than as zero-spend days. A fully-elapsed past month is unaffected.
    const lastDay =
      currentMonth === today.slice(0, 7)
        ? Number(today.slice(8, 10))
        : daysInMonth;
    const scoped = selected
      ? monthExpenses.filter((t) => t.category === selected)
      : monthExpenses;

    // One pass into day buckets, rather than rescanning the month once per day.
    const byDay = new Map<number, number>();
    for (const t of scoped) {
      const day = Number(t.date.slice(8, 10));
      byDay.set(day, (byDay.get(day) || 0) + Math.abs(reportableBase(t) ?? 0));
    }

    const dailyData: HeatmapDay[] = [];
    for (let day = 1; day <= lastDay; day++)
      dailyData.push({ day, spending: roundMoney(byDay.get(day) || 0) });
    return { dailyData, daysInMonth };
  }, [monthExpenses, currentMonth, selected, today]);

  const total = dailyData.reduce((sum, d) => sum + d.spending, 0);
  // Gridlines are placed on round values rather than left to divide the data range evenly.
  // Recharts' own ticks land on values like 4,500, which the "k" label then rounded to "5k" —
  // so a 5,000 point appeared to float above its own gridline.
  const axis = useMemo(() => {
    const peak = Math.max(...dailyData.map((d) => d.spending), 0);
    if (peak <= 0) return { max: 100, ticks: [0, 100] };
    const rough = peak / 5;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const step =
      magnitude * (rough / magnitude <= 1 ? 1 : rough / magnitude <= 2 ? 2 : rough / magnitude <= 5 ? 5 : 10);
    const max = Math.ceil(peak / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= max + step / 2; v += step) ticks.push(v);
    return { max, ticks };
  }, [dailyData]);
  const lineData = useMemo(() => dailyData.map((d) => ({ ...d, label: String(d.day) })), [dailyData]);
  const isCurrentMonth = currentMonth === today.slice(0, 7);
  return (
    <section className={`spending-chart-container overview-module${collapsed ? " is-collapsed" : ""}`}>
      <div className="section-heading">
        <div>
          <h2>{selected || "Daily spending"}</h2>
          <p>
            <strong><Private>{money(total, baseCurrency)}</Private></strong> spent
            <span className="spending-period-copy">
              {isCurrentMonth ? " so far this month" : " this month"}
            </span>
          </p>
        </div>
        <div className="overview-heading-actions">{categories.length > 0 && (
          <label className="spending-chart-category-picker">
            <span className="sr-only">Show category</span>
            <select
              value={selected}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value={OVERALL}>Overall</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        )}<div className="category-chart-toolbar">
          <button aria-label="Show calendar heatmap" title="Calendar heatmap" aria-pressed={chartStyle === "heatmap"} onClick={() => onChartStyleChange("heatmap")}><CalendarDays size={15} /></button>
          <button aria-label="Show line chart" title="Line chart" aria-pressed={chartStyle === "line"} onClick={() => onChartStyleChange("line")}><ChartLine size={15} /></button>
        </div><button className="overview-collapse-button" aria-label={`${collapsed ? "Expand" : "Collapse"} Daily spending`} aria-expanded={!collapsed} onClick={onToggle}><ChevronDown size={17} /></button></div>
      </div>
      {!collapsed && (chartStyle === "line"
        ? <Suspense fallback={<div className="chart-loading" role="status">Loading chart…</div>}><DailySpendingPlot data={lineData} axis={axis} baseCurrency={baseCurrency} /></Suspense>
        : <DailySpendingHeatmap days={dailyData} month={currentMonth} daysInMonth={daysInMonth} baseCurrency={baseCurrency} />)}
    </section>
  );
}
